import ReactDOM from "react-dom";
import { CheckCircle2, XCircle, ChevronRight, X } from "lucide-react";
import { WORKFLOW_STEPS, STATUS_META } from "./constants";
import { daysUntil } from "../../utils/format";
import { useCerrarConAtras } from "../../utils/useCerrarConAtras";

// BLOQUE 17 — daysUntil ahora vive en utils/format.js (fuente única, parseo date-only sin
// desfase de huso horario). Se re-exporta acá para no romper los ~10 imports existentes
// desde "./SharedComponents".
export { daysUntil };

export function getWfIndex(status) {
  const s = STATUS_META[status]?.wfKey || status;
  const idx = WORKFLOW_STEPS.findIndex(w => w.key === s);
  return idx === -1 ? 0 : idx;
}

export function DeadlineBadge({ deadline, status }) {
  // FIX 2 — los presupuestos ya enviados/aprobados/rechazados cumplieron su
  // flujo: el deadline de envío ya no aplica, no mostramos "Venció".
  if (["enviado", "aprobado", "rechazado"].includes(status)) return null;
  const days = daysUntil(deadline);
  if (days === null) return null;
  if (days < 0) return <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-red-600 text-white">Venció</span>;
  if (days === 0) return <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-red-500 text-white animate-pulse">¡Hoy!</span>;
  if (days <= 3) return <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-red-100 text-red-700 animate-pulse">{days}d</span>;
  if (days <= 7) return <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-amber-100 text-amber-700">{days}d</span>;
  return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">{days}d</span>;
}

export function WorkflowStepper({ status }) {
  if (status === "rechazado") {
    return (
      <div className="flex items-center gap-1.5 text-xs text-red-600 font-semibold">
        <XCircle size={13} /> Presupuesto rechazado
      </div>
    );
  }
  const activeIdx = getWfIndex(status);
  return (
    <div className="flex items-center flex-wrap gap-0 gap-y-1" data-testid="workflow-stepper">
      {WORKFLOW_STEPS.map((step, i) => {
        const done = i < activeIdx;
        const active = i === activeIdx;
        return (
          <div key={step.key} className="flex items-center">
            <div className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold transition-all
              ${active ? "bg-[#2E6DA4] text-white shadow-sm" :
                done ? "bg-emerald-100 text-emerald-700" :
                "bg-gray-100 text-gray-400"}`}>
              {done ? <CheckCircle2 size={10} /> : null}
              <span style={{ fontSize: "0.6rem" }}>{step.short}</span>
            </div>
            {i < WORKFLOW_STEPS.length - 1 && (
              <ChevronRight size={10} className={done || active ? "text-[#2E6DA4]" : "text-gray-300"} />
            )}
          </div>
        );
      })}
    </div>
  );
}

export function Modal({ open, onClose, title, children, wide, fullscreen }) {
  // Fix AI (2026-09-04): el botón "atrás" del navegador cierra este modal.
  useCerrarConAtras(open, onClose);
  if (!open) return null;
  const sizeClass = fullscreen
    ? "w-full h-full rounded-none max-h-full"
    : wide ? "max-w-6xl max-h-[95vh]" : "max-w-lg max-h-[90vh]";
  return ReactDOM.createPortal(
    <div className={`fixed inset-0 z-50 flex items-center justify-center ${fullscreen ? "" : "p-2 sm:p-4"}`}>
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className={`relative bg-white rounded-2xl shadow-2xl w-full ${sizeClass} overflow-hidden flex flex-col`}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <h2 className="text-base font-bold text-gray-900">{title}</h2>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 transition-colors">
            <X size={18} className="text-gray-500" />
          </button>
        </div>
        <div className="overflow-y-auto flex-1">{children}</div>
      </div>
    </div>,
    document.body
  );
}
